import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { installedItems } from "@p0security/shared/install/installed";
import { otherInstalled } from "@p0security/shared/install/options";
import { Instructions } from "@p0security/shared/install/types";
import { AzureComponents } from "@p0security/shared/integrations/resources/azure/components";
import { azureBastionHostComponentSetupFor } from "@p0security/shared/integrations/resources/azure/helper";
import { AzureIntegration } from "@p0security/shared/integrations/resources/azure/types";
import { widetype } from "@p0security/shared/util/collections";
import { Typography } from "antd";
import { FrontendInstallContext, FrontendInstaller } from "install/types";
import { ReactNode } from "react";

const azureBastionHostInstructionsFor = (
  context: FrontendInstallContext<AzureIntegration>,
  id: string
): Instructions<ReactNode> => {
  const azureApp = installedItems("azure-app", context.config);
  const { clientId, clientServicePrincipalId } = azureApp[SINGLETON_ITEM] ?? {};
  if (!clientId || !clientServicePrincipalId) {
    throw new Error("Azure App Registration is not installed.");
  }
  const { customRole } = azureBastionHostComponentSetupFor(id);
  const {
    name: roleName,
    description,
    isCustom,
    actions,
    assignableScope,
  } = customRole;
  const transformedActions = JSON.stringify(actions, null, 2);
  const shell = [
    {
      header: "Target the subscription bastion host is being setup on",
      command: `az account set --subscription ${id}`,
    },
    {
      header: "Create a P0 Bastion service management role",

      command: `
    ${"" /** TODO: Check if the role already exists */}
      # Create a JSON file for the role definition
  cat > p0-bastion-service-management-role.json << 'EOF'
{
  "Name": "${roleName}",
  "IsCustom": ${isCustom},
  "Description": "${description}",
  "Actions": ${transformedActions},
  "NotActions": [],
  "AssignableScopes": [
        "${assignableScope}"
  ]
}
EOF

# Create the custom role
az role definition create --role-definition  p0-bastion-service-management-role.json`,
    },
    {
      header:
        "Create role assignment of service principal to new P0 Management Role in the current subscription",
      command: `az role assignment create --assignee ${clientId} --role "${roleName}" --scope ${assignableScope}`,
    },
  ];
  const iac = [
    {
      header: "Create a P0 Management Role",
      command: `resource "azurerm_role_definition" "p0_azure_custom_role_for_bastion_host" {
  name       = "${roleName}"
  scope       = "${assignableScope}"
  description = "${description}"

  permissions {
    actions     = ${transformedActions}
    not_actions = []
  }

  assignable_scopes = [
    "${assignableScope}"
  ]
}
    `,
    },
    {
      header:
        "Create role assignment of service principal to new P0 bastion host service management role in the current subscription",
      command: `
resource "azurerm_role_assignment" "p0_role_assignment" {
  depends_on         = [azurerm_role_definition.p0_azure_custom_role_for_bastion_host]
  scope              = "${assignableScope}"
  role_definition_id = azurerm_role_definition.p0_azure_custom_role_for_bastion_host.role_definition_resource_id
  principal_type     = "ServicePrincipal"

  principal_id = "${clientServicePrincipalId}"
}`,
    },
  ];
  return {
    help: (
      <Typography.Paragraph>
        In order to provision SSH access to your Azure subscription, we need to
        create a custom role and assign it to the Azure App Registration
        you&apos;ve already integrated with P0.
      </Typography.Paragraph>
    ),
    commands: {
      shell,
      iac,
    },
  };
};

export const azureBastionHostInstaller: FrontendInstaller<
  typeof AzureComponents
>["bastion-host"] = {
  instructions: azureBastionHostInstructionsFor,
  optionProvider: async (context) => {
    const iamWriteItems =
      otherInstalled<AzureIntegration>("iam-write")(context);
    const alreadyInstalled = widetype.keys(
      context.config["bastion-host"] ?? {}
    );
    const filteredItems = iamWriteItems.filter(
      (item) => !alreadyInstalled.includes(item.id)
    );
    return filteredItems;
  },

  items: {
    bastion: {
      options: {
        single: {
          items: {},
        },
        subscription: {
          items: {
            subscriptionId: {
              optionProvider: async (context, id) => {
                const items = widetype
                  .entries(installedItems("bastion-host", context.config))
                  .filter(
                    ([key, value]) =>
                      key !== id && value?.bastion.type === "single"
                  )
                  .map(([id, value]) => ({
                    id,
                    label: value?.label ?? id,
                  }));

                return items;
              },
            },
          },
        },
      },
    },
  },
};
